const { getElement, getElements } = window.BAO.Utils

class PushCartError {
  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle (error, variantId) {
    const instance = new this(error)

    if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC, variantId)
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT, variantId)
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK, variantId)
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK, variantId)
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT, variantId)
    } else if (instance.error.message) {
      return instance.render(instance.error.message, variantId)
    } else {
      return instance.render(instance.MESSAGES.ADD_TOO_MANY, variantId)
    }
  }

  constructor (error) {
    this.error = error

    this.MESSAGES = {
      ADD_TOO_MANY: 'Unfortunately, there isn\'t enough of this product available',
      ALREADY_HAVE_ALL_STOCK: 'You already have all of the remaining stock in your cart',
      CANNOT_FIND_VARIANT: 'There was an error adding this product to cart.',
      GENERIC: 'There was an error adding this product to cart.',
      NOT_ENOUGH_IN_STOCK: 'Unfortunately, there isn\'t enough of this product available',
      SOLD_OUT: 'Unfortunately, this product is sold out.',
    }

    this.description = error?.description ?? ''
    this.status = error?.status ?? null

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement('push-cart')
  }

  render (message, variantId = null) {
    if (variantId) {
      const hasVariant = getElements('cart-item', { context: this.pushCart }).find((item) => item.dataset.id === variantId)

      if (hasVariant) {
        const clone = this.pushCart.els['error-template-line'].element.content.cloneNode(true)
        const text = getElement(this.selectors.text, {
          context: clone,
        })

        text.textContent = message

        hasVariant.els.errors.element.appendChild(clone)

        return this
      }
    }

    const clone = this.pushCart.els['error-template'].element.content.cloneNode(true)
    const text = getElement(this.selectors.text, {
      context: clone,
    })

    text.textContent = message

    this.pushCart.els.errors.element.appendChild(clone)

    return this
  }

  get addedMoreThanExists () {
    return this.description.slice(0, 18) === "You can't add more"
  }

  get alreadyHaveAllInCart () {
    return this.description.slice(0, 4) === 'All '
  }

  get genericError () {
    return this.error?.errors?.toLowerCase() === ' error'
  }

  get soldOut () {
    return this.description.slice(0, 13) === "The product '"
  }

  get cannotFindVariant () {
    return this.description.toLowerCase() === 'cannot find variant'
  }

  get selectors () {
    return {
      item: '[data-push-cart-errors-item]',
      text: '[data-push-cart-errors-text]',
      close: '[data-push-cart-errors-close]',
    }
  }
}

export class PushCart extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['error-template', 'errors']
  }

  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)
  }

  constructor () {
    super()

    this.sections = window.BAO.sectionRenderer.pushCartSections

    if (localStorage.getItem('openMinicartOnRedirect') === window.location.href) {
      window.drawers.activeDrawerKey = 'cart'

      localStorage.removeItem('openMinicartOnRedirect')
    }
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this)
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this)
    this.onCartChanged = this.onCartChanged.bind(this)
    this.updatePopup = this.updatePopup.bind(this)

    if (this.externalEls['login-link'] && this.externalEls['login-link'].element) {
      this.externalEls['login-link'].element.addEventListener('click', () => {
        localStorage.setItem('loggedInFromLocation', window.location.href)
      })
    }

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.TOLSTOY_CHANGED,
      this.onCartChanged,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      (event) => {
        if ('sections' in event.detail.state) {
          this.renderSections(event.detail.state.sections)
          this.loading = false
        }
      },
    )
  }

  updatePopup () {
    const animation = this.externalEls.popup?.element?.querySelector('#erase')
    if (animation) {
      animation.beginElement()
    }

    window.drawers.activeDrawerKey = 'add-to-cart-popup'
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.removeEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
  }

  onBeforeCartAdd () {
    if (this.getAttribute('data-atc-notification') === 'popup' && window.innerWidth <= 768) {
      this.updatePopup()
    } else {
      window.drawers.activeDrawerKey = 'cart'
    }

    this.loading = true
  }

  onAfterCartAdd (event) {
    if ('sections' in event.detail.state) {
      this.renderSections(event.detail.state.sections)
    }

    this.loading = false
  }

  async onCartChanged (event) {
    const state = await window.BAO.Cart.refreshCart()

    const newState = {
      detail: {
        state,
      },
    }

    this.onAfterCartAdd(newState)

    window.drawers.activeDrawerKey = 'cart'
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError (res, variantId) {
    this.loading = false

    return PushCartError.handle(await res.json(), variantId)
  }

  get loadingClass () {
    return this.getAttribute('loading-class') || 'is-loading'
  }
}

if (!customElements.get('push-cart')) {
  customElements.define('push-cart', PushCart)
}
